import {
	Globe,
	Building2,
	PanelLeftClose,
	PanelLeft,
	SidebarClose,
	SidebarOpen,
} from "lucide-react";
import { useEffect } from "react";
import { useCatalogsStore } from "../../store/Catalogs";
import { useCentralStore } from "../../store/Central";
import { CatalogsList } from "./components/catalogs-list.component";
import { CategoriesList } from "./components/categories-list.component";
import { TariffsTable } from "./components/tariffs-table.component";
import { CatalogDialog } from "./components/catalog-dialog.component";
import { CategoryDialog } from "./components/category-dialog.component";
import { TariffDialog } from "./components/tariff-dialog.component";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { getLocalStorage } from "@/lib/utils/local-storage";

export const CatalogsPage = () => {
	const {
		catalogs,
		categories,
		filteredTariffs,
		selectedCatalogId,
		selectedCategoryId,
		isLoadingCatalogs,
		isLoadingCategories,
		isLoadingTariffs,
		showCatalogsList,
		showCategoriesList,
		selectCatalog,
		selectCategory,
		toggleCatalogsList,
		toggleCategoriesList,
		visiblePriceListIds,
		setVisiblePriceLists,
	} = useCatalogsStore();

	const priceLists = useCentralStore((state) => state.priceLists);

	const selectedCatalog =
		selectedCatalogId === "all-catalogs"
			? null
			: catalogs.find((c) => c.id === selectedCatalogId);
	const isAllCatalogs = selectedCatalogId === "all-catalogs";

	// Set visible price lists when they become available and none are visible yet
	useEffect(() => {
		if (priceLists.length > 0 && visiblePriceListIds.size === 0) {
			const savedPriceListIds = getLocalStorage<string[]>(
				"catalogs-visible-price-lists",
				[]
			);
			if (savedPriceListIds.length === 0) {
				setVisiblePriceLists(priceLists.slice(0, 3).map((pl) => pl.id));
			}
		}
	}, [priceLists, visiblePriceListIds.size, setVisiblePriceLists]);

	return (
		<div className="h-full flex flex-col">
			<div className="grid grid-cols-12 flex-1 overflow-hidden">
				{/* Catalogs Section - Left sidebar */}
				{showCatalogsList && (
					<div className="col-span-2 bg-white border-r border-gray-200 flex flex-col min-h-0 overflow-auto">
						<div className="p-4 border-b border-gray-200 flex justify-between items-center">
							<h2 className="text-lg font-semibold text-gray-900">
								Kataloge
							</h2>
							<div className="flex items-center gap-2">
								<CatalogDialog />
							</div>
						</div>
						<div className="flex-1 overflow-auto">
							<CatalogsList
								catalogs={catalogs}
								selectedCatalogId={selectedCatalogId}
								isLoading={isLoadingCatalogs}
								onSelectCatalog={selectCatalog}
							/>
						</div>
					</div>
				)}

				{/* Categories and Tariffs Section - Main content area */}
				<div
					className={cn(
						"bg-white flex flex-col flex-1 overflow-hidden",
						showCatalogsList ? "col-span-10" : "col-span-12"
					)}
				>
					{selectedCatalog || isAllCatalogs ? (
						<>
							<div className="p-4 border-b border-gray-200 flex justify-between items-center">
								<div className="flex items-center gap-2">
									<Button
										variant="ghost"
										size="icon"
										onClick={toggleCatalogsList}
										title={
											showCatalogsList
												? "Kataloge ausblenden"
												: "Kataloge anzeigen"
										}
										className="h-8 w-8"
									>
										{showCatalogsList ? (
											<PanelLeftClose className="h-4 w-4" />
										) : (
											<PanelLeft className="h-4 w-4" />
										)}
									</Button>
									<div>
										<h2 className="text-lg font-semibold text-gray-900">
											{isAllCatalogs
												? "Alle Kataloge"
												: selectedCatalog?.name}
										</h2>
										<p className="text-sm text-gray-500 mt-1">
											{isAllCatalogs
												? "Zeigt Leistungen aus allen Katalogen"
												: selectedCatalog?.catalog_type ===
													  "custom"
													? "Benutzerdefinierter Katalog"
													: "Standardkatalog"}
										</p>
									</div>
								</div>
							</div>

							<div className="flex-1 flex min-h-0">
								{/* Categories - Left side */}
								{showCategoriesList && !isAllCatalogs && (
									<div className="w-1/3 border-r border-gray-200 flex flex-col min-h-0 overflow-auto">
										<div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
											<h3 className="text-sm font-medium text-gray-700">
												Kategorien
											</h3>
											<div className="flex items-center gap-2">
												{selectedCatalogId &&
													selectedCatalog && (
														<CategoryDialog
															catalogId={
																selectedCatalogId
															}
															catalogName={
																selectedCatalog.name ||
																""
															}
															isCustomCatalog={
																selectedCatalog.catalog_type ===
																"custom"
															}
														/>
													)}
											</div>
										</div>
										<div className="flex-1 overflow-auto">
											<CategoriesList
												categories={categories}
												selectedCategoryId={
													selectedCategoryId
												}
												isLoading={isLoadingCategories}
												onSelectCategory={
													selectCategory
												}
											/>
										</div>
									</div>
								)}

								{/* Tariffs - Right side */}
								<div
									className={cn(
										"flex flex-col min-h-0 overflow-auto",
										showCategoriesList && !isAllCatalogs
											? "flex-1"
											: "w-full"
									)}
								>
									<div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
										<div className="flex items-center gap-2">
											{!isAllCatalogs && (
												<Button
													variant="ghost"
													size="icon"
													onClick={
														toggleCategoriesList
													}
													title={
														showCategoriesList
															? "Kategorien ausblenden"
															: "Kategorien anzeigen"
													}
													className="h-7 w-7"
												>
													{showCategoriesList ? (
														<SidebarClose className="h-3.5 w-3.5" />
													) : (
														<SidebarOpen className="h-3.5 w-3.5" />
													)}
												</Button>
											)}
											<h3 className="text-sm font-medium text-gray-700">
												Leistungen
												{selectedCategoryId &&
													categories.length > 0 && (
														<span className="ml-2 text-gray-500">
															(
															{
																categories.find(
																	(c) =>
																		c.id ===
																		selectedCategoryId
																)?.name
															}
															)
														</span>
													)}
											</h3>
										</div>
										{selectedCatalogId &&
											selectedCatalog && (
												<TariffDialog
													catalogId={
														selectedCatalogId
													}
													catalogName={
														selectedCatalog.name ||
														""
													}
													categoryId={
														selectedCategoryId
													}
													categoryName={
														categories.find(
															(c) =>
																c.id ===
																selectedCategoryId
														)?.name
													}
													isCustomCatalog={
														selectedCatalog.catalog_type ===
														"custom"
													}
												/>
											)}
									</div>
									<div className="flex-1 min-h-0">
										<TariffsTable
											tariffs={filteredTariffs}
											isLoading={isLoadingTariffs}
											showCatalogColumn={isAllCatalogs}
										/>
									</div>
								</div>
							</div>
						</>
					) : (
						<div className="flex-1 p-8">
							<div className="max-w-6xl mx-auto">
								<h2 className="text-lg font-semibold text-gray-900 mb-6">
									Katalog auswählen
								</h2>
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
									{/* Catalog cards */}
									{catalogs.map((catalog) => (
										<div
											key={catalog.id}
											onClick={() =>
												selectCatalog(catalog.id)
											}
											className={cn(
												"group relative flex flex-col rounded-xl border border-gray-200 p-5 cursor-pointer transition-all hover:border-gray-300 hover:shadow-sm",
												catalog.is_deactivated &&
													"opacity-60"
											)}
										>
											<div className="flex flex-col h-full">
												<div className="flex items-start gap-3 mb-3">
													{catalog.catalog_type ===
													"custom" ? (
														<Building2 className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0 mt-0.5" />
													) : (
														<Globe className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0 mt-0.5" />
													)}
													<div className="flex-1 min-w-0">
														<h3 className="font-semibold text-base group-hover:text-blue-600 transition-colors truncate">
															{catalog.name}
														</h3>
														<p className="text-sm text-gray-500 mt-1">
															{catalog.catalog_type ===
															"custom"
																? "Benutzerdefiniert"
																: "Standardkatalog"}
														</p>
													</div>
												</div>
												{catalog.is_deactivated && (
													<span className="mt-auto inline-flex w-fit items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 ring-1 ring-inset ring-gray-200">
														Deaktiviert
													</span>
												)}
											</div>
										</div>
									))}

									{/* Add new catalog card */}
									<CatalogDialog asCard />
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
